import axios, { AxiosError } from "axios";
import * as moment from "moment";

import { ParentReferralReportModel } from "@teamprevent/common/workAdjustment/models";
import { AnsweredQuestionWithId, Questionnaire } from "@teamprevent/common/questionnaires/model";

import { OhCaseDetailModel } from "../crud/ohCase/model";
import { ChangeWorkAdjustmentCaseSecondaryStatusModel } from "../crud/ohCase/components/details/workAdjustment/models";

const buildUrl = () => "/api/workAdjustment";

const getParentReferralReport = async (parentReferralCaseId: string, writeReferralReportTaskId: string) => {
    try {
        const response = await axios.get<ParentReferralReportModel>(
            `${buildUrl()}/${parentReferralCaseId}/parentReferralReport/${writeReferralReportTaskId}`);
        return parseParentReferralReportModel(response.data);
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError?.response?.status === 404) {
            return null;
        }

        throw ex;
    }
};

const getInitialQuestionnaire = async (workAdjustmentCaseId: string, initialWorkAdjustmentDetailsTaskId: string) => {
    try {
        const response = await axios.get<Questionnaire>(`${buildUrl()}/${workAdjustmentCaseId}/initialWorkAdjustment/${initialWorkAdjustmentDetailsTaskId}`);
        return response.data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError?.response?.status === 404) {
            return null;
        }

        throw ex;
    }
};

const getUpdateQuestionnaire = async (workAdjustmentCaseId: string, updateWorkAdjustmentTaskId: string) => {
    try {
        const response = await axios.get<Questionnaire>(`${buildUrl()}/${workAdjustmentCaseId}/update/${updateWorkAdjustmentTaskId}`);
        return response.data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError?.response?.status === 404) {
            return null;
        }

        throw ex;
    }
};

const getRecommendedEndDateOfAdjustment = async (workAdjustmentCaseId: string, initialWorkAdjustmentDetailsTaskId: string) => {
    try {
        const response = await axios.get<string>(
            `${buildUrl()}/${workAdjustmentCaseId}/initialWorkAdjustment/${initialWorkAdjustmentDetailsTaskId}/endDateOfAdjustment`);
        return response.data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError?.response?.status === 404) {
            return null;
        }

        throw ex;
    }
};

const updateWorkAdjustmentQuestionnaire = async (workAdjustmentCaseId: string, updateWorkAdjustmentTaskId: string, questions: AnsweredQuestionWithId[]) => {
    try {
        const response = await axios.post(`${buildUrl()}/${workAdjustmentCaseId}/update/${updateWorkAdjustmentTaskId}`, { questions });
        return response.data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError?.response?.status === 400) {
            const error = { message: axiosError?.response?.data, status: 400 };
            throw error;
        }
        else if (axiosError?.response?.status === 404) {
            return null;
        }

        throw ex;
    }
};

const changeWorkAdjustmentCaseSecondaryStatus = async (workAdjustmentCaseId: string, model: ChangeWorkAdjustmentCaseSecondaryStatusModel) => {
    try {
        const response = await axios.put<OhCaseDetailModel>(`${buildUrl()}/${workAdjustmentCaseId}/changeSecondaryStatus`, model);
        return response.data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError?.response?.status === 400) {
            const error = { message: axiosError?.response?.data, status: 400 };
            throw error;
        }
        else if (axiosError?.response?.status === 404) {
            return null;
        }

        throw ex;
    }
};

const parseParentReferralReportModel = (model: ParentReferralReportModel) => {
    return {
        ...model,
        dateOfBirth: model.dateOfBirth && moment(model.dateOfBirth),
        dateOfReport: model.dateOfReport && moment(model.dateOfReport),
        recommendedEndDate: model.recommendedEndDate && moment(model.recommendedEndDate),
        advisedTargetReturnToWorkDate: model.advisedTargetReturnToWorkDate && moment(model.advisedTargetReturnToWorkDate)
    };
};

export const workAdjustmentApi = {
    getParentReferralReport,
    getInitialQuestionnaire,
    getUpdateQuestionnaire,
    getRecommendedEndDateOfAdjustment,
    updateWorkAdjustmentQuestionnaire,
    changeWorkAdjustmentCaseSecondaryStatus
};
